import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Text,
  Stack,
  Grid,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  Progress,
  Alert,
  ThemeIcon,
  Image,
  ActionIcon,
  Menu,
  rem,
} from '@mantine/core';
import {
  IconActivity,
  IconPhoto,
  IconDownload,
  IconBell,
  IconClock,
  IconHeart,
  IconShield,
  IconMail,
  IconDots,
  IconEye,
  IconChevronRight,
  IconPlayerPlay,
  IconCheck,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useParentChildren } from '../../hooks/parent/useParentChildren';
import { useTodayActivities } from '../../hooks/parent/useChildActivities';
import { useAuth } from '../../contexts/AuthContext';
import ActivityTimeline from '../../components/activities/ActivityTimeline';
import { SmartLoader } from '../../components/common/LoadingStates';

const ParentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const {
    data: childrenResponse,
    isLoading: childrenLoading,
    error: childrenError,
  } = useParentChildren();

  // Fix data structure handling - API returns data in .items property
  const children = Array.isArray(childrenResponse?.items) ? childrenResponse.items : [];
  const selectedChild = children.length > 0
    ? (children.find((child: any) => child.id === selectedChildId) || children[0])
    : null;

  // Auto-select first child when children are loaded
  useEffect(() => {
    if (children.length > 0 && !selectedChildId) {
      setSelectedChildId(children[0].id);
    }
  }, [children, selectedChildId]);

  const {
    data: activitiesResponse,
    isLoading: activitiesLoading,
  } = useTodayActivities(selectedChild?.id);

  // Fix data structure handling for activities
  const todayActivities = Array.isArray(activitiesResponse?.items)
    ? activitiesResponse.items
    : [];

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleDownloadReport = () => {
    notifications.show({
      title: 'Download Started',
      message: 'Daily report is being prepared for download',
      color: 'blue',
    });
  };

  const handleContactTeacher = () => {
    notifications.show({
      title: 'Message Sent',
      message: 'Your message has been sent to the teacher',
      color: 'green',
    });
  };

  const getTodayStats = () => {
    if (!todayActivities || todayActivities.length === 0) return { total: 0, meals: 0, play: 0, rest: 0 };

    return {
      total: todayActivities.length,
      meals: todayActivities.filter((a: any) => a.activity_type === 'meal' || a.activity_type === 'eating').length,
      play: todayActivities.filter((a: any) => a.activity_type === 'play' || a.activity_type === 'playing').length,
      rest: todayActivities.filter((a: any) => a.activity_type === 'sleep' || a.activity_type === 'nap' || a.activity_type === 'rest').length,
    };
  };

  const stats = getTodayStats();

  return (
    <Stack gap="lg">
      {/* Welcome Header */}
      <Card withBorder radius="lg" p="xl" style={{ background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)' }}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} fw={700} c="white">
              {getTimeBasedGreeting()}, {user?.first_name || 'Parent'}!
            </Title>
            <Text c="rgba(255,255,255,0.9)" size="lg" mt="xs">
              Here's how your {children?.length === 1 ? 'child is' : 'children are'} doing today
            </Text>
          </div>
          <Group gap="md">
            <Badge size="lg" variant="white" color="dark">
              Parent Portal
            </Badge>
            <ThemeIcon size="xl" radius="xl" variant="white" color="dark">
              <IconHeart size={24} />
            </ThemeIcon>
          </Group>
        </Group>
      </Card>

      {/* Child Selector */}
      {children && children.length > 1 && (
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Select Child</Title>
            <Text size="sm" c="dimmed">{children.length} children</Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {children.map((child: any) => (
              <Paper
                key={child.id}
                withBorder
                p="md"
                radius="md"
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedChild?.id === child.id ? 'var(--mantine-color-blue-0)' : undefined,
                  borderColor: selectedChild?.id === child.id ? 'var(--mantine-color-blue-6)' : undefined,
                }}
                onClick={() => setSelectedChildId(child.id)}
              >
                <Group>
                  <Avatar size="lg" radius="xl">
                    {child.first_name?.[0] || 'C'}{child.last_name?.[0] || 'H'}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <Text fw={500}>
                      {child.first_name || 'Child'} {child.last_name || ''}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {child.date_of_birth
                        ? `Age ${new Date().getFullYear() - new Date(child.date_of_birth).getFullYear()}`
                        : 'Age not available'
                      }
                    </Text>
                    <Badge variant="light" color="green" size="xs" mt={2}>
                      {child.status || 'Active'}
                    </Badge>
                  </div>
                  {selectedChild?.id === child.id && (
                    <ThemeIcon color="blue" variant="light">
                      <IconCheck size={16} />
                    </ThemeIcon>
                  )}
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>
      )}

      <SmartLoader
        isLoading={childrenLoading || (children.length > 0 && !selectedChild)}
        error={childrenError as Error | null}
        isEmpty={!childrenLoading && children.length === 0}
        emptyState={
          <Alert color="blue" title="No children found">
            No children are associated with your account. Please contact the school administration.
          </Alert>
        }
      >
        {selectedChild && (
          <>
            {/* Child Overview */}
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <Avatar size="xl" radius="xl">
                    {selectedChild.first_name[0]}{selectedChild.last_name[0]}
                  </Avatar>
                  <div>
                    <Title order={3}>{selectedChild.first_name} {selectedChild.last_name}</Title>
                    <Text size="sm" c="dimmed" mb="xs">
                      Age {selectedChild.date_of_birth ? new Date().getFullYear() - new Date(selectedChild.date_of_birth).getFullYear() : 'Unknown'} •
                      Class {(selectedChild as any).class_name || 'A'}
                    </Text>
                    <Group gap="xs">
                      <Badge color="green" variant="light">Present Today</Badge>
                      <Badge color="blue" variant="light">No Alerts</Badge>
                    </Group>
                  </div>
                </Group>
                <Group>
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="lg">
                        <IconDots size={20} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => navigate(`/parent/child/${selectedChild.id}`)}
                      >
                        View Full Profile
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                        onClick={handleDownloadReport}
                      >
                        Download Report
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconMail style={{ width: rem(14), height: rem(14) }} />}
                        onClick={handleContactTeacher}
                      >
                        Contact Teacher
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPhoto style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => navigate(`/parent/child/${selectedChild.id}/photos`)}
                      >
                        View Photos
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Group>
            </Card>

            {/* Today's Summary */}
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Card withBorder>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>Today's Activities</Title>
                    <Group gap="xs">
                      <Badge variant="light" color="blue">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      </Badge>
                      <Button
                        variant="subtle"
                        size="xs"
                        rightSection={<IconChevronRight size={14} />}
                        onClick={() => navigate(`/parent/child/${selectedChild.id}/activities`)}
                      >
                        View All
                      </Button>
                    </Group>
                  </Group>

                  <SimpleGrid cols={4} spacing="md" mb="lg">
                    <div style={{ textAlign: 'center' }}>
                      <ThemeIcon size="lg" variant="light" color="blue" mx="auto" mb="xs">
                        <IconActivity size={20} />
                      </ThemeIcon>
                      <Text fw={700} size="xl">{stats.total}</Text>
                      <Text size="xs" c="dimmed">Total Activities</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <ThemeIcon size="lg" variant="light" color="orange" mx="auto" mb="xs">
                        <IconSun size={20} />
                      </ThemeIcon>
                      <Text fw={700} size="xl">{stats.meals}</Text>
                      <Text size="xs" c="dimmed">Meals</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <ThemeIcon size="lg" variant="light" color="green" mx="auto" mb="xs">
                        <IconPlayerPlay size={20} />
                      </ThemeIcon>
                      <Text fw={700} size="xl">{stats.play}</Text>
                      <Text size="xs" c="dimmed">Play Time</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <ThemeIcon size="lg" variant="light" color="violet" mx="auto" mb="xs">
                        <IconMoon size={20} />
                      </ThemeIcon>
                      <Text fw={700} size="xl">{stats.rest}</Text>
                      <Text size="xs" c="dimmed">Rest Time</Text>
                    </div>
                  </SimpleGrid>

                  <SmartLoader isLoading={activitiesLoading} isEmpty={!todayActivities?.length}>
                    {selectedChild && (
                      <ActivityTimeline
                        studentId={selectedChild.id}
                        student={selectedChild}
                        maxItems={5}
                        showFilters={false}
                        showHeader={false}
                        dateRange={{
                          start: new Date(),
                          end: new Date(),
                        }}
                      />
                    )}
                  </SmartLoader>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="md">
                  {/* Quick Stats */}
                  <Card withBorder>
                    <Title order={5} mb="md">Today's Highlights</Title>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="green">
                            <IconClock size={12} />
                          </ThemeIcon>
                          <Text size="sm">Arrival Time</Text>
                        </Group>
                        <Text size="sm" fw={500}>8:30 AM</Text>
                      </Group>

                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="blue">
                            <IconHeart size={12} />
                          </ThemeIcon>
                          <Text size="sm">Mood</Text>
                        </Group>
                        <Badge color="green" variant="light" size="sm">Happy</Badge>
                      </Group>

                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="orange">
                            <IconSun size={12} />
                          </ThemeIcon>
                          <Text size="sm">Meals Eaten</Text>
                        </Group>
                        <Text size="sm" fw={500}>{stats.meals}/3</Text>
                      </Group>

                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="violet">
                            <IconMoon size={12} />
                          </ThemeIcon>
                          <Text size="sm">Nap Time</Text>
                        </Group>
                        <Text size="sm" fw={500}>1:00 - 2:30 PM</Text>
                      </Group>

                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="sm" variant="light" color="red">
                            <IconShield size={12} />
                          </ThemeIcon>
                          <Text size="sm">Safety Score</Text>
                        </Group>
                        <Badge color="green" variant="light" size="sm">Excellent</Badge>
                      </Group>
                    </Stack>
                  </Card>

                  {/* Teacher's Note */}
                  <Card withBorder>
                    <Title order={5} mb="md">Teacher's Note</Title>
                    <Text size="sm" style={{ fontStyle: 'italic' }} mb="md">
                      "{selectedChild.first_name} had a wonderful day today! They were very engaged during art time
                      and showed great sharing skills during group play. Keep up the great work!"
                    </Text>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed">Ms. Johnson, Lead Teacher</Text>
                      <Text size="xs" c="dimmed">Today, 3:15 PM</Text>
                    </Group>
                  </Card>

                  {/* Quick Actions */}
                  <Card withBorder>
                    <Title order={5} mb="md">Quick Actions</Title>
                    <Stack gap="xs">
                      <Button
                        variant="light"
                        leftSection={<IconPhoto size={16} />}
                        fullWidth
                        onClick={() => navigate(`/parent/child/${selectedChild.id}/photos`)}
                      >
                        View Today's Photos
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconDownload size={16} />}
                        fullWidth
                        onClick={handleDownloadReport}
                      >
                        Download Daily Report
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconMail size={16} />}
                        fullWidth
                        onClick={handleContactTeacher}
                      >
                        Message Teacher
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconBell size={16} />}
                        fullWidth
                        onClick={() => navigate('/parent/notifications')}
                      >
                        Notification Settings
                      </Button>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>

            {/* Recent Photos */}
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <Title order={4}>Recent Photos</Title>
                <Button
                  variant="subtle"
                  size="sm"
                  rightSection={<IconChevronRight size={14} />}
                  onClick={() => navigate(`/parent/child/${selectedChild.id}/photos`)}
                >
                  View All Photos
                </Button>
              </Group>

              <SimpleGrid cols={{ base: 2, sm: 4, md: 6 }} spacing="md">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <Image
                      src={null}
                      alt={`Photo ${index + 1}`}
                      fallbackSrc="https://via.placeholder.com/150x150?text=Photo"
                      radius="md"
                      h={100}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/parent/child/${selectedChild.id}/photos`)}
                    />
                    <Badge
                      size="xs"
                      variant="filled"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                      }}
                    >
                      Today
                    </Badge>
                  </div>
                ))}
              </SimpleGrid>
            </Card>

            {/* Weekly Progress */}
            <Card withBorder>
              <Title order={4} mb="md">This Week's Progress</Title>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Social Skills</Text>
                      <Text size="sm" fw={500}>Excellent</Text>
                    </Group>
                    <Progress value={90} color="green" size="sm" radius="xl" mb="md" />
                  </div>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Learning Engagement</Text>
                      <Text size="sm" fw={500}>Very Good</Text>
                    </Group>
                    <Progress value={85} color="blue" size="sm" radius="xl" mb="md" />
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Physical Activity</Text>
                      <Text size="sm" fw={500}>Good</Text>
                    </Group>
                    <Progress value={75} color="orange" size="sm" radius="xl" mb="md" />
                  </div>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Emotional Well-being</Text>
                      <Text size="sm" fw={500}>Excellent</Text>
                    </Group>
                    <Progress value={95} color="violet" size="sm" radius="xl" mb="md" />
                  </div>
                </Grid.Col>
              </Grid>
            </Card>
          </>
        )}
      </SmartLoader>
    </Stack>
  );
};

export default ParentDashboard;
